import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './App.css';
import FileDropzone from './components/FileDropzone';
import DocTypeSelector, { emptyField, SelectorState } from './components/DocTypeSelector';
import ResultViewer from './components/ResultViewer';
import { getDocTypes, postOcr, OcrApiError } from './api/ocrClient';
import { DocTypeInfo, OcrOptions, OcrResponse } from './types';

// Fallback list if /doc-types can't be reached (e.g. auth not configured in dev).
const FALLBACK_DOC_TYPES: DocTypeInfo[] = [
  { id: 'generic', label: 'Generic', description: 'Auto-detect and extract all fields, tables and text.', fields: [] },
  { id: 'invoice', label: 'Invoice', description: 'Vendor invoices and bills.', fields: [] },
  { id: 'bill', label: 'Bill (NetSuite)', description: 'Vendor bills/invoices with clean line items. Works for any vendor, language or currency.', fields: [] },
  { id: 'receipt', label: 'Receipt', description: 'Point-of-sale and expense receipts.', fields: [] },
  { id: 'purchase_order', label: 'Purchase Order', description: 'Purchase orders.', fields: [] },
  { id: 'business_card', label: 'Business Card', description: 'Contact details from a business card.', fields: [] },
  { id: 'id_document', label: 'ID Document', description: 'Passport, national ID or driving licence.', fields: [] },
];

const App: React.FC = () => {
  const [docTypes, setDocTypes] = useState<DocTypeInfo[]>(FALLBACK_DOC_TYPES);
  const [files, setFiles] = useState<File[]>([]);
  const [selector, setSelector] = useState<SelectorState>({
    // Bills are what this app exists for, so start there.
    mode: 'bill',
    customFields: [emptyField()],
    prompt: '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<OcrResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getDocTypes()
      .then((types) => {
        if (active && types.length) setDocTypes(types);
      })
      .catch(() => {
        /* keep fallback list — extraction can still proceed */
      });
    return () => {
      active = false;
    };
  }, []);

  const validCustomFields = useMemo(
    () => selector.customFields.filter((f) => f.name.trim()),
    [selector.customFields]
  );

  const canSubmit =
    files.length > 0 && !loading && (selector.mode !== 'custom' || validCustomFields.length > 0);

  const handleSubmit = useCallback(async () => {
    if (files.length === 0) return;
    setLoading(true);
    setError(null);
    setResult(null);

    const options: OcrOptions = {};
    if (selector.prompt.trim()) options.prompt = selector.prompt.trim();
    if (selector.mode === 'custom') {
      options.schema = { fields: validCustomFields };
    } else if (selector.mode !== 'generic') {
      options.docType = selector.mode;
    }

    try {
      const res = await postOcr(files, options);
      setResult(res);
    } catch (err) {
      const message =
        err instanceof OcrApiError
          ? `${err.message}${err.code ? ` (${err.code})` : ''}`
          : 'Something went wrong while contacting the OCR service.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [files, selector, validCustomFields]);

  const publicUrl = process.env.PUBLIC_URL || '';

  return (
    <div className="app">
      <div className="topbar">
        <img
          className="topbar__pfc"
          src={`${publicUrl}/PFC_LOGO.png`}
          alt="Pro Futuro Consulting"
        />
        <img
          className="topbar__zoho"
          src={`${publicUrl}/zoho_advanced_partner_v1.png`}
          alt="Zoho Advanced Partner"
        />
      </div>

      <header className="hero">
        <h1>AI OCR Reader</h1>
        <p>Upload a PDF or image and extract structured data with Gemini AI.</p>
      </header>

      <main className="app__grid">
        <section className="panel panel--input">
          <h2>1 · Documents</h2>
          <FileDropzone files={files} onChange={setFiles} disabled={loading} />

          <h2 className="panel__step">2 · What to extract</h2>
          <DocTypeSelector
            docTypes={docTypes}
            value={selector}
            onChange={setSelector}
            disabled={loading}
          />

          <button
            type="button"
            className="btn btn--primary"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {loading
              ? 'Reading…'
              : files.length > 1
                ? `Extract data from ${files.length} files`
                : 'Extract data'}
          </button>

          {error && <p className="banner banner--error">{error}</p>}
        </section>

        <section className="panel panel--result">
          {result ? (
            <ResultViewer response={result} />
          ) : (
            <div className="placeholder">
              <div className="placeholder__icon">{loading ? '⏳' : '📄'}</div>
              <p className="muted">
                {loading
                  ? 'Extracting structured data…'
                  : 'Extracted results will appear here — as a clear table, or as JSON for developers.'}
              </p>
            </div>
          )}
        </section>
      </main>

      <footer className="app__footer">
        <span>A Pro Futuro Consulting solution</span>
        <span className="app__footer-sep">·</span>
        <span>Powered by Google Gemini</span>
        <span className="app__footer-sep">·</span>
        <span>Served by Zoho Catalyst</span>
      </footer>
    </div>
  );
};

export default App;
